import {
	ColumnHeader,
	IPageRange,
	Loading,
	PageTurnOptions,
	PagingOptions,
	SearchBox,
	Table,
	ToolMenu,
	ToolMenuContent,
} from '@aon/aon-tws-common-ui';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IApplicationState } from '../../../store';
import { ColumnHelper } from '../../../utilities';
import { UserListRow } from './UserListRow';
import { ActionCreators } from '../../../store/SurveyCopyModalStore';
import { LoadingWrapper } from '@aon/tws-common-ui-questions';

const sortableColumns: string[] = ['id', 'firstName', 'lastName', 'status'];

export interface IUserListTableProps {
	selectedUserIds: number[];
	setSelectedUserIds: (ids: number[]) => void;
}

export const UserListTable = (props: IUserListTableProps) => {
	const dispatch = useDispatch();
	const clientId = useSelector((state: IApplicationState) => state.oidc.user?.profile.external_clientid);
	const { columns, isLoadingUsers, userCount, users } = useSelector((state: IApplicationState) => state.surveyCopyModal);

	const [pagingParams, setPaging] = useState<IPageRange>({
		offset: 0,
		limit: 25,
		total: userCount,
	});
	const [searchText, setSearchText] = useState('');
	const [sortingParams, setSortParams] = useState({
		columns: ColumnHelper.GetUpdateColumnHeaders(columns, sortableColumns, true),
		sortedBy: 'UserId',
		sortAsc: true,
	});

	useEffect(() => {
		return setPaging({ ...pagingParams, total: userCount });
	}, [userCount]);
	useEffect(() => {
		dispatch(ActionCreators.GetUserCountByClientId(clientId, searchText));
	}, [searchText]);

	const sortValue = sortingParams.sortedBy + '.' + (sortingParams.sortAsc ? 'asc' : 'desc');
	useEffect(() => {
		dispatch(ActionCreators.GetUsersByClientId(clientId, searchText, pagingParams, sortValue));
	}, [searchText, pagingParams.offset, pagingParams.limit, sortingParams.sortedBy, sortingParams.sortAsc]);

	const userIdsInTable = users.map((u) => u.userId);
	const selectedIdsInTable = props.selectedUserIds.filter((id) => userIdsInTable.indexOf(id) > -1);
	const isSelectAllChecked = selectedIdsInTable.length === userIdsInTable.length;

	return (
		<>
			<>
				<PagingOptions pageRange={pagingParams} updatePageRange={setPaging} />
				<ToolMenu>
					<ToolMenuContent type={'right'}>
						<SearchBox callbackFunc={setSearchText} resultsCount={users.length} initialSearchText={searchText} inputSize={'sm'} />
					</ToolMenuContent>
				</ToolMenu>
				<Table responsive={true} lastalign={'right'} selectable={'true'}>
					<ColumnHeader
						columns={ColumnHelper.GetColumnHeaders(columns)}
						selectable={true}
						headerCheckboxSortable={false}
						showHeaderCheckbox={true}
						handleSort={(sortedBy: string, sortAscByDefault: boolean) => SortOnColumn(sortedBy, sortAscByDefault)}
						sortedBy={sortingParams.sortedBy}
						sortAsc={sortingParams.sortAsc}
						onHeaderCheckboxChange={(value: boolean) =>
							SetUserIds(
								users.map((u) => u.userId),
								value,
							)
						}
						headerCheckboxChecked={isSelectAllChecked}
					/>
					{isLoadingUsers ? (
						<LoadingWrapper>
							<Loading height='70px' width='70px' />
						</LoadingWrapper>
					) : (
						<tbody>
							{users.map((u, index) => (
								<UserListRow
									key={index}
									user={u}
									columns={columns}
									onSelectRow={(isSelected: boolean) => SetUserIds([u.userId], !isSelected)}
									isSelected={props.selectedUserIds.indexOf(u.userId) > -1}
								/>
							))}
						</tbody>
					)}
				</Table>
				<PageTurnOptions pageRange={pagingParams} updatePageRange={setPaging} />
			</>
		</>
	);

	function SortOnColumn(sortedBy: string, sortAscByDefault: boolean) {
		let sortAsc = !sortingParams.sortAsc;
		if (sortingParams.sortedBy !== sortedBy) {
			sortAsc = sortAscByDefault;
		}
		setSortParams({
			columns: sortingParams.columns,
			sortedBy: sortedBy,
			sortAsc: sortAsc,
		});
	}

	function SetUserIds(incomingUserIds: number[], isSelected: boolean) {
		let updatedUsers = [...props.selectedUserIds];
		if (isSelected) {
			const missingIds = incomingUserIds.filter((id) => props.selectedUserIds.indexOf(id) === -1);
			updatedUsers = updatedUsers.concat(missingIds);
		} else {
			updatedUsers = props.selectedUserIds.filter((id) => incomingUserIds.indexOf(id) === -1);
		}
		props.setSelectedUserIds(updatedUsers);
	}
};
